import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { AxiosError } from "axios";
import {
  proceduresService,
  CreateProcedureInput,
  UpdateProcedureInput,
} from "../../services/admin/procedures.service";

/**
 * Extract a user-friendly error message from API errors.
 * Specifically handles duplicate key constraint violations.
 */
function getErrorMessage(error: unknown, defaultMessage: string): string {
  if (error instanceof AxiosError) {
    const responseData = error.response?.data;

    // Check for duplicate key error from backend
    if (responseData?.error === "DUPLICATE_NAME") {
      return (
        responseData.message || "A procedure with this name already exists"
      );
    }

    // Check for PostgreSQL unique constraint violation (code 23505)
    // This handles cases where backend doesn't catch the error
    if (
      error.response?.status === 500 &&
      (responseData?.message?.includes("duplicate key") ||
        responseData?.message?.includes("unique constraint") ||
        responseData?.error?.includes("duplicate key") ||
        responseData?.error?.includes("unique constraint") ||
        error.message?.includes("duplicate key"))
    ) {
      return "A procedure with this name already exists. Please use a different name.";
    }

    // Return backend-provided message if available
    if (responseData?.message) {
      return responseData.message;
    }
  }

  // Fallback to error message or default
  if (error instanceof Error) {
    return error.message || defaultMessage;
  }

  return defaultMessage;
}

export function useProceduresAdmin() {
  return useQuery({
    queryKey: ["admin", "procedures"],
    queryFn: proceduresService.getAll,
  });
}

export function useProcedure(id?: string) {
  return useQuery({
    queryKey: ["admin", "procedures", id],
    queryFn: () => proceduresService.getById(id!),
    enabled: !!id,
  });
}

export function useCreateProcedure() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateProcedureInput) =>
      proceduresService.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "procedures"] });
      queryClient.invalidateQueries({ queryKey: ["procedures"] }); // Also invalidate non-admin cache
      notifications.show({
        title: "Success",
        message: "Procedure created successfully",
        color: "green",
      });
    },
    onError: (error: unknown) => {
      notifications.show({
        title: "Error",
        message: getErrorMessage(error, "Failed to create procedure"),
        color: "red",
      });
    },
  });
}

export function useUpdateProcedure() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateProcedureInput) =>
      proceduresService.update(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "procedures"] });
      queryClient.invalidateQueries({ queryKey: ["procedures"] });
      notifications.show({
        title: "Success",
        message: "Procedure updated successfully",
        color: "green",
      });
    },
    onError: (error: unknown) => {
      notifications.show({
        title: "Error",
        message: getErrorMessage(error, "Failed to update procedure"),
        color: "red",
      });
    },
  });
}

export function useDeleteProcedure() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => proceduresService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "procedures"] });
      queryClient.invalidateQueries({ queryKey: ["procedures"] });
      notifications.show({
        title: "Success",
        message: "Procedure deleted successfully",
        color: "green",
      });
    },
    onError: (error: unknown) => {
      notifications.show({
        title: "Error",
        message: getErrorMessage(error, "Failed to delete procedure"),
        color: "red",
      });
    },
  });
}

export function useToggleProcedureActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      proceduresService.toggleActive(id, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "procedures"] });
      queryClient.invalidateQueries({ queryKey: ["procedures"] });
      notifications.show({
        title: "Success",
        message: "Status updated successfully",
        color: "green",
      });
    },
    onError: (error: unknown) => {
      notifications.show({
        title: "Error",
        message: getErrorMessage(error, "Failed to update status"),
        color: "red",
      });
    },
  });
}
